import React from 'react'
import styled from '@emotion/styled'
import { createDraggable, eases } from 'animejs'
import { layering, radius, shadow } from '@mobi/component-library/Foundations'
import { debounce } from '@mobi/utils/functions'
import { store } from '@core/Store'
import { useAppDispatch } from '@core/Store/hooks'
import { keys as analyticsKeys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { trackKey } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { setIsControlShowing, setIsPlaying } from '@core/Areas/SkyVideoPlayer/Store'
import { selectIsVideoPlaying } from '@core/Areas/SkyVideoPlayer/Store/selectors'
import { VideoPlayer } from '../VideoPlayer/VideoPlayer'
import { findAndStartChildVideoElement } from '../VideoPlayer/helpers'
import { usePlayerState } from './helpers/usePlayerState'

export const FloatingPlayer: React.FC = () => {
  const [orientation, setOrientation] = React.useState<'landscape' | 'portrait'>(() =>
    getOrientation()
  )

  // Listen to Screen Orientation Change Event
  React.useEffect(() => {
    let timerId: number
    const handleOrientationChange = () => {
      timerId && clearTimeout(timerId)
      timerId = window.setTimeout(() => setOrientation(getOrientation()), 500)
    }
    window.addEventListener('orientationchange', handleOrientationChange)
    return () => {
      clearTimeout(timerId)
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [])

  return <FloatingPlayerComponent key={orientation} orientation={orientation} />
}

// ===============
// Local Component
// ===============

const FloatingPlayerComponent: React.FC<{ orientation: 'landscape' | 'portrait' }> = ({
  orientation,
}) => {
  const dispatch = useAppDispatch()

  const floatingElRef = React.useRef<HTMLDivElement>(null)
  const resizeHandleElRef = React.useRef<HTMLDivElement>(null)

  const { position, width, handleSetPosition, handleSetWidth } = usePlayerState(orientation)

  /**
   * Set Up Floating Player Draggable
   */
  React.useEffect(() => {
    if (!floatingElRef.current) return

    let resizeTimerRef: number | null = null
    const handleSetPositionDebounced = debounce(handleSetPosition, 300)

    const videoDraggable = createDraggable(floatingElRef.current, {
      container: 'html',
      maxVelocity: 10,
      velocityMultiplier: 0.5,
      releaseStiffness: 300,
      onGrab: handleSetPositionDebounced.cancel,
      onSettle(self) {
        trackKey(analyticsKeys.visionPositionChanged)
        handleSetPositionDebounced({ x: self.x, y: self.y })
      },
      onAfterResize(self) {
        resizeTimerRef = window.setTimeout(() => {
          const allowedVisiblePx = 50
          const { top, bottom, left, right } = self.$target.getBoundingClientRect()
          const isOutsideViewport =
            top >= window.innerHeight - allowedVisiblePx ||
            left >= window.innerWidth - allowedVisiblePx ||
            bottom <= allowedVisiblePx ||
            right <= allowedVisiblePx
          if (isOutsideViewport) {
            self.setX(0)
            self.setY(0)
            handleSetWidth('RESET')
            handleSetPosition('RESET')
          }
        }, 2_000)
      },
    })
    return () => {
      resizeTimerRef && clearTimeout(resizeTimerRef)
      videoDraggable.revert()
    }
  }, [dispatch, handleSetPosition, handleSetWidth])

  /**
   * Set Up Player Resize Draggable
   */
  React.useEffect(() => {
    if (!resizeHandleElRef.current || !floatingElRef.current) return

    let startingWidth: number | null = null
    const videoPlayerEl = floatingElRef.current

    const resizeDraggable = createDraggable(resizeHandleElRef.current, {
      container: [0, 0, 0, 0],
      maxVelocity: 0,
      containerFriction: 1,
      releaseEase: eases.none,
      y: false,
      x: { modifier: value => value * 2 },
      onGrab() {
        startingWidth = videoPlayerEl.getBoundingClientRect().width
      },
      onDrag(self) {
        if (startingWidth === null) return

        const [x] = self.coords
        const { width: currWidth, height } = videoPlayerEl.getBoundingClientRect()
        const newWidth = Math.round(Math.abs(-startingWidth + x))

        const doesHeightExceedViewport = newWidth > currWidth && window.innerHeight - 50 <= height
        if (doesHeightExceedViewport) return

        const doesNewWidthFitWithinClient = newWidth <= document.documentElement.clientWidth
        if (doesNewWidthFitWithinClient) handleSetWidth(newWidth + 'px')
      },
    })
    return () => {
      resizeDraggable.revert()
    }
  }, [handleSetWidth])

  const handleVideoPlayerClick = React.useCallback<React.MouseEventHandler<HTMLDivElement>>(
    e => {
      if (!shouldAllowControlToggle(e.target as Element, e.currentTarget as Element)) return
      if (selectIsVideoPlaying(store.getState())) {
        dispatch(setIsControlShowing(null))
      } else {
        dispatch(setIsPlaying(true))
      }
      findAndStartChildVideoElement(floatingElRef.current)
    },
    [dispatch]
  )

  return (
    <FloatingPlayerWrapperStyled
      ref={floatingElRef}
      onClick={handleVideoPlayerClick}
      style={{ width, transform: `translateX(${position.x}px) translateY(${position.y}px)` }}
    >
      <div ref={resizeHandleElRef} data-js-resize-handle />
      <VideoPlayer />
    </FloatingPlayerWrapperStyled>
  )
}

// =============
// Local Helpers
// =============

function shouldAllowControlToggle(target: Element | null, parent: Element): boolean {
  if (!target) return true
  return !parent.contains(target.closest('button'))
}

function getOrientation(): 'landscape' | 'portrait' {
  return isScreenLandscape() ? 'landscape' : 'portrait'
}

function isScreenLandscape() {
  if (window.matchMedia('(orientation: landscape)')?.matches) {
    return true
  }

  const isLandscape = (angle: number) => [-90, 90].includes(angle)
  const orientation = window.screen?.orientation?.angle || window.orientation

  if (typeof orientation === 'number') {
    return isLandscape(orientation)
  }

  if (typeof orientation === 'string') {
    return (orientation as string).toLowerCase() === 'landscape'
  }

  return false
}

// ======
// Styles
// ======

const FloatingPlayerWrapperStyled = styled.div({
  position: 'fixed',
  bottom: '7rem',
  right: '1rem',
  zIndex: layering.skyVideoPlayerFloating,

  minWidth: '25rem',
  borderRadius: radius.lgx1,
  boxShadow: shadow.lg,
  overflow: 'hidden',

  '[data-js-resize-handle]': {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 5,
    // Same width + height as PlayerControlButtonStyled
    width: '4.4rem',
    height: '4.4rem',
  },
})
